// Strong ETag computation that byte-for-byte matches the backend's
// `strongEtag(version)` (packages/domain): `"` + base64url(SHA-256(String(version))) + `"`.
//
// The members list and seat projections don't carry per-row ETag response headers,
// but every conflict-prone resource exposes a numeric `version`. The backend derives
// the If-Match target from that version, so the browser reconstructs the same value.
// See apps/web/CONTRACT_NOTES.md (N-1).

import { base64UrlFromBytes } from "./base64";

export async function strongEtag(version: number | string): Promise<string> {
  const data = new TextEncoder().encode(String(version));
  const digest = await crypto.subtle.digest("SHA-256", data);
  return `"${base64UrlFromBytes(new Uint8Array(digest))}"`;
}

// Registry of ETags captured from response headers, keyed by an opaque resource key
// (e.g. `saml:<orgId>`, `settings:<orgId>`). Used for resources the server *does*
// emit an ETag header for (SAML connection, settings, dashboard).
const registry = new Map<string, string>();

export function rememberEtag(key: string, etag: string | null | undefined): void {
  if (!etag) return;
  // Cloudflare weakens origin ETags when it may transform a response body. These
  // validators are version tokens rather than representation hashes, and the
  // origin's If-Match check still expects the strong form it emitted. Restore
  // that form before reusing the validator on the next mutation.
  registry.set(key, etag.startsWith("W/") ? etag.slice(2) : etag);
}

export function getEtag(key: string): string | undefined {
  return registry.get(key);
}

export function forgetEtag(key: string): void {
  registry.delete(key);
}

export function clearEtags(): void {
  registry.clear();
}
